import { FC, memo, useContext, useRef } from 'react';
import { apiV2EnergyOverviewGreenEnergyPost } from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';

import styles from './index.module.scss';
import { Progress } from 'antd';
import { formatKgToTonUnit, formatThousandAndUnit, keepOneOrLine } from '@/utils/utils';
import { ShareDataWrapperContext } from '../../../../index';
import AiAssistant from '../../../AiAssistant';

interface ICenterBottomBox {
  className?: string;
  showAssistant?: boolean;
}

const AI_ASSISTANT_SIZE = { width: 81, height: 108 };
const AI_ASSISTANT_LARGE_SIZE = { width: 90, height: 120 };

const CenterBottomBox: FC<ICenterBottomBox> = ({ className, showAssistant = true }) => {
  const { data } = useRequest(apiV2EnergyOverviewGreenEnergyPost);
  const { scale: pageScale } = useContext(ShareDataWrapperContext);
  const assistantAnchorRef = useRef<HTMLDivElement>(null);
  const isCenterBottomBox = className === 'centerBottomBox' || className === styles.centerBottomBox;
  const rootClassName = className ? styles[className] || className : '';

  return (
    <div className={`${styles.bottomRow} ${rootClassName}`}>
      <div className={`${styles.item}`}>
        <div className={styles.title}>绿色能源</div>
        <div className={styles.infoList}>
          <div className={styles.infoItem}>
            <span className={styles.subTitle}>本年光伏发电(kWh)</span>
            <span className={styles.value}>
              {formatThousandAndUnit(data?.pvPower).value}
              <span className={styles.unit}>{formatThousandAndUnit(data?.pvPower).unit}</span>
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.subTitle}>光伏发电占比</span>
            <div className={styles.value}>
              <Progress
                type="circle"
                size={28}
                percent={data?.pvPowerRate ?? 0}
                trailColor="#4A90E233"
                showInfo={false}
                strokeColor="#5BFF60"
              />
              {keepOneOrLine(data?.pvPowerRate) + '%'}
            </div>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.subTitle}>节约电费(元)</span>
            <div className={styles.value}>
              {formatThousandAndUnit(data?.saveElectricity).value}{' '}
              <span className={styles.unit}>{formatThousandAndUnit(data?.saveElectricity).unit}</span>
            </div>
          </div>
        </div>
      </div>
      <div className={`${styles.item}`}>
        <div className={styles.title}>社会价值</div>
        <div className={styles.infoList}>
          <div className={styles.infoItem}>
            <span className={styles.subTitle}>本年碳减排({formatKgToTonUnit(data?.carbonReduction)?.unit}CO2e)</span>
            <div className={styles.value}>{formatKgToTonUnit(data?.carbonReduction)?.value}</div>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.subTitle}>节约标准煤({formatKgToTonUnit(data?.saveStandardCoal)?.unit}ce)</span>
            <div className={styles.value}>{formatKgToTonUnit(data?.saveStandardCoal)?.value}</div>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.subTitle}>相当于植树(棵)</span>
            <div className={styles.value}>
              {formatThousandAndUnit(data?.treeNum, 1, true).value}{' '}
              <span className={styles.unit}>{formatThousandAndUnit(data?.treeNum).unit}</span>
            </div>
          </div>
        </div>
      </div>
      {showAssistant && (
        <>
          <div
            ref={assistantAnchorRef}
            className={`${styles.ai_robot_anchor} ${isCenterBottomBox ? styles.ai_robot_anchor_large : ''}`}
            aria-hidden
          />
          <AiAssistant
            anchorRef={assistantAnchorRef}
            placement="bottom-align"
            baseSize={isCenterBottomBox ? AI_ASSISTANT_LARGE_SIZE : AI_ASSISTANT_SIZE}
            pageScale={pageScale}
          />
        </>
      )}
    </div>
  );
};

export default memo(CenterBottomBox);
